/**
 * The client for the relay and the corpus API.
 *
 * The browser never talks to a model provider — the relay owns the key, the
 * cap and the system prompt. Everything here goes to our own origin.
 */

import { UserFacingError, responseSentence } from '@shared/user-errors.js'
import { GROUNDED, type Mode } from './mode'
import { withReadingSession } from './readingSession'

// standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
// Python constant, so the single home stays the server and this mirrors it. The
// paydown is generating the vocabulary into TypeScript rather than carrying a marker
// per site -- see docs/engineering-standards.md.
export type ClaimStatus = 'text-asserted' | 'inferred' | 'open'
export type ClaimBasis = 'text' | 'lexicon' | 'commentary' | 'synthesis'

export interface Claim {
  claim: string
  status: ClaimStatus
  basis: ClaimBasis
  references: string[]
  versions: string[]
  tier: number | null
}

export interface Turn {
  role: 'user' | 'assistant'
  content: string
}

export interface AnswerSummary {
  claims: Claim[]
  /** Which corpus answered. A finding is meaningless without it. */
  corpus: string
  /**
   * Which discipline produced this answer, stamped by the relay.
   *
   * Per message rather than per session, because the mode rides each ask and a
   * transcript may legitimately mix the two.
   */
  mode: Mode
  consulted_versions: string[]
  /** Versions the tools actually returned; the session set grows by these. */
  read_versions?: string[]
  /** Sentences for the reader about codes the server dropped. */
  active_notes?: string[]
  /** The set as the server resolved it: the session set is rebuilt from this, so a dropped code leaves it. */
  active_codes?: string[]
  cited_references: string[]
  crossed_a_border: boolean
  questions_remaining: number
}

export interface Verse {
  chapter: number
  verse: number
  /**
   * How this row is marked in its chapter or provision: `1`, or `stk. 2`.
   *
   * Empty where the source marks nothing — a § with a single stykke. Sent by
   * the server rather than assembled here: the spelling belongs to the corpus's
   * reference grammar, and a client that rebuilt it from the numbers would be a
   * second implementation of it, free to drift.
   */
  marker: string
  text: string
}

export interface VersionPassage {
  code: string
  name: string
  language: string
  tier: number
  attribution: string
  /** The CC licence's name ("CC BY-SA 4.0") and its deed, which CC 4.0 asks to be linked; null for public domain. */
  licence_name: string | null
  licence_url: string | null
  verses: Verse[]
}

export interface Passage {
  reference: string
  book: string
  /** The public page this passage is read on (same origin), or null where none shows it. */
  page: string | null
  notes: string[]
  versions: VersionPassage[]
}

export interface LexiconEntry {
  key: string
  strongs: string
  language: string
  lemma: string
  transliteration: string
  morphology: string
  gloss: string
  entry: string
  source: string
  tier: number
}

export interface LexiconResult {
  query: string
  notes: string[]
  entries: LexiconEntry[]
  occurrence_count: number
  occurrences: { reference: string; surface: string; gloss: string }[]
}

export interface Tier {
  ordinal: number
  name: string
  description: string
}

export interface CorpusInfo {
  code: string
  name: string
  /** `biblical` reads `Rom 8:28`; `statute` reads `§ 5, stk. 2`. */
  reference_grammar: string
  /**
   * Whether this corpus may be asked in research mode, and so whether the
   * composer draws the toggle. The relay decides the same question again when
   * the ask arrives: this is what to draw, not what is permitted.
   */
  allow_research: boolean
  /** Where the generated pages list every book (same origin), for "Read the text". */
  book_index: string
  tiers: Tier[]
  /** The version a conversation starts from when the reader has saved no start set. */
  primary_version_code: string
  versions: {
    code: string
    name: string
    language: string
    /** 'text' or 'commentary'. Only a text version can join the active set. */
    role: string
    /** Always in the reader's version row and never removable: the original and the primary translation. */
    pinned: boolean
    /** May stand in the reader's row (`can_be_active`): the reading pages offer exactly these too. */
    addable: boolean
    tier: number
    attribution: string
  }[]
}

/**
 * Why a request failed, as the server classified it.
 *
 * The status code cannot carry this on its own: the daily cap and the rate
 * limiter are both 429 and mean opposite things — one is over until midnight,
 * the other is over in a minute. The server sends the distinction explicitly.
 */
export type FailureKind =
  | 'unconfigured'
  | 'provider'
  | 'internal'
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  | 'cap_reached'
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  | 'rate_limited'
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  | 'bad_request'
  // A delete refused because other findings or threads stand on the target.
  // Its own kind because the useful response is specific: offer to retire it
  // instead, which is what the study framework does with a claim no longer held.
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  | 'blocked_by_dependents'
  // Today's free questions are used and the wallet cannot cover another. A 402,
  // and the reader has two ways on — top up, or their own AI key — so the
  // notice links to the account page where both live.
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  | 'payment_required'
  // A save refused because somebody else saved the record first. A 409 with the
  // current record attached; the editors show it beside the reader's form.
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  | 'edit_conflict'
  // This reading session has used today's passage lookups. Over at midnight UTC;
  // distinct from `cap_reached`, which is the AI-question allowance.
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  | 'lookup_cap_reached'
  // No reading session. `readingSession.ts` answers it by starting one; a caller
  // only ever sees it if starting failed.
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  | 'visitor_session_required'

/** Mirrors MAX_QUESTION_LENGTH, so the textarea stops what the API would reject. */
export const MAX_QUESTION_LENGTH = 2_000

/** Events the relay emits while answering. */
export type ChatEvent =
  | { type: 'retrieving'; tool: string }
  | { type: 'delta'; text: string }
  | { type: 'done'; summary: AnswerSummary }
  | { type: 'error'; message: string; kind: FailureKind }

/** A failed lookup, worded for the reader: the server's sentence, or one chosen by status. */
class ApiError extends UserFacingError {}

async function getJson<T>(path: string): Promise<T> {
  const response = await withReadingSession(() => fetch(path, { headers: { accept: 'application/json' } }))
  if (!response.ok) {
    const body: { detail?: unknown } = await response.json().catch(() => ({}))
    throw new ApiError(responseSentence(body.detail, response.status))
  }
  return response.json() as Promise<T>
}

export const getCorpus = (): Promise<CorpusInfo> => getJson<CorpusInfo>('/api/corpus')

/** `versions` null asks for every version the reader may see. */
export const getPassage = (reference: string, versions: string[] | null = null): Promise<Passage> =>
  getJson<Passage>(
    `/api/passage?reference=${encodeURIComponent(reference)}` +
      (versions === null ? '' : `&versions=${encodeURIComponent(versions.join(','))}`),
  )

export const getLexicon = (word: string): Promise<LexiconResult> =>
  getJson<LexiconResult>(`/api/lexicon?word=${encodeURIComponent(word)}`)

/**
 * An answer may legitimately take a minute; beyond this something is wrong.
 * Without it the only way out of a hung request was the reader pressing Stop.
 */
const ANSWER_TIMEOUT_MS = 180_000

function withTimeout(signal: AbortSignal): AbortSignal {
  // AbortSignal.any is recent enough to be worth guarding; without it the
  // reader keeps manual Stop, which is what they had before.
  if (typeof AbortSignal.any !== 'function' || typeof AbortSignal.timeout !== 'function') {
    return signal
  }
  return AbortSignal.any([signal, AbortSignal.timeout(ANSWER_TIMEOUT_MS)])
}

/**
 * Send the question and return the answer's byte stream — or the error event a
 * reader is shown instead, when there is no stream to read.
 *
 * Starting a reading session can fail on its own (the bot check, the network);
 * that is reported like any other refusal, and nothing was charged.
 */
async function openAnswer(
  question: string,
  history: Turn[],
  signal: AbortSignal,
  mode: Mode,
  activeVersions: string[] | null,
): Promise<NonNullable<Response['body']> | ChatEvent> {
  let response: Response
  try {
    response = await withReadingSession(() =>
      fetch('/api/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ question, history, mode, active_versions: activeVersions }),
        signal: withTimeout(signal),
      }),
    )
  } catch (error) {
    if (signal.aborted) throw error
    return {
      type: 'error',
      message: error instanceof Error ? error.message : String(error),
      // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
      // Python constant, so the single home stays the server and this mirrors it. The
      // paydown is generating the vocabulary into TypeScript rather than carrying a marker
      // per site -- see docs/engineering-standards.md.
      kind: 'bad_request',
    }
  }

  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    return {
      type: 'error',
      message: detail.detail ?? response.statusText,
      kind: failureKind(response.status, detail.kind),
    }
  }
  if (!response.body) {
    return { type: 'error', message: 'The connection produced no response body.', kind: 'internal' }
  }
  return response.body
}

/**
 * Ask a question, yielding events as the answer is written.
 *
 * Server-sent events are parsed by hand rather than with EventSource, because
 * EventSource cannot issue a POST — and the question, plus the whole
 * conversation the browser owns, has to travel in a body rather than a URL.
 */
export async function* ask(
  question: string,
  history: Turn[],
  signal: AbortSignal,
  mode: Mode = GROUNDED,
  activeVersions: string[] | null = null,
): AsyncGenerator<ChatEvent> {
  const opened = await openAnswer(question, history, signal, mode, activeVersions)
  if ('type' in opened) {
    yield opened
    return
  }

  const reader = opened.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += value

    // Events are separated by a blank line; a partial event stays in the
    // buffer until the rest of it arrives.
    let boundary = buffer.indexOf('\n\n')
    while (boundary !== -1) {
      const chunk = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)
      const parsed = parseEvent(chunk)
      if (parsed) yield parsed
      boundary = buffer.indexOf('\n\n')
    }
  }
}

/**
 * Trust the server's own classification; fall back to the status code.
 *
 * The fallback only runs against a response our API did not produce — an
 * nginx error page, say. A 429 defaults to rate-limited rather than to the
 * cap, because the cap always names itself and nothing else can.
 */
function failureKind(status: number, kind: unknown): FailureKind {
  if (typeof kind === 'string') return kind as FailureKind
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  if (status === 429) return 'rate_limited'
  if (status === 503) return 'unconfigured'
  if (status === 502) return 'provider'
  // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
  // Python constant, so the single home stays the server and this mirrors it. The
  // paydown is generating the vocabulary into TypeScript rather than carrying a marker
  // per site -- see docs/engineering-standards.md.
  if (status >= 400 && status < 500) return 'bad_request'
  return 'internal'
}

/**
 * The two lines an SSE frame is made of.
 *
 * Separate from interpreting them because it is a different question: this one is about the
 * wire format and knows nothing about what the stream carries, so a frame that arrives
 * without both halves is simply not a frame yet.
 */
function readEventFrame(chunk: string): { name: string; data: string } | null {
  let name = ''
  let data = ''
  for (const line of chunk.split('\n')) {
    if (line.startsWith('event: ')) name = line.slice(7)
    else if (line.startsWith('data: ')) data = line.slice(6)
  }
  if (!name || !data) return null

  return { name, data }
}

/** One frame's payload as the event it names. An unknown name is ignored, never thrown on. */
function toChatEvent(name: string, payload: Record<string, unknown>): ChatEvent | null {
  switch (name) {
    case 'retrieving':
      return { type: 'retrieving', tool: payload.tool as string }
    case 'delta':
      return { type: 'delta', text: payload.text as string }
    case 'done':
      return { type: 'done', summary: payload as unknown as AnswerSummary }
    case 'error':
      return {
        type: 'error',
        message: payload.message as string,
        kind: (payload.kind as FailureKind) ?? 'internal',
      }
    default:
      return null
  }
}

function parseEvent(chunk: string): ChatEvent | null {
  const frame = readEventFrame(chunk)
  if (frame === null) return null

  try {
    return toChatEvent(frame.name, JSON.parse(frame.data) as Record<string, unknown>)
  } catch {
    // A frame whose data is not JSON is a stream we cannot read, not a reason to stop
    // reading: the relay's own events are well-formed, so this is something in between.
    return null
  }
}
